import CHOICES from "CHOICES";
import { mutate } from "utils/relay";
import {
  AUTH_TOKEN,
  ENV_MAPPER,
  TOKEN_TTL,
  REFRESH_TOKEN,
} from "utils/constants";
import graphql from "babel-plugin-relay/macro";
import { emptyProxyObject, getEnv, ProxyObjectType } from "utils/utils";
import { Action, Thunk, Computed, thunk, action, computed } from "easy-peasy";
import { Cookies } from "react-cookie";
import moment from "moment";

const mutation = graphql`
  mutation authRefreshMutation($input: RefreshInput!) {
    refreshToken(input: $input) {
      token
      payload
      refreshToken
    }
  }
`;

const cookie = new Cookies();

const cookieDomain = (env: string) =>
  env === ENV_MAPPER.production
    ? ".v3.nuflights.com"
    : env === ENV_MAPPER.development
      ? `.${env}.nuflights.com`
      : `.jwt.${env}.nuflights.com`;

const UK = CHOICES.UserKind;

export interface CurrentUserModel {
  username: string;
  email: string;
}

export interface AuthModel {
  currentUser: CurrentUserModel | ProxyObjectType;
  authToken: string | null;
  refreshToken: string | null;
  isLoggedIn: Computed<AuthModel, boolean>;
  isAdmin: Computed<AuthModel, boolean>;
  setAuthToken: Action<AuthModel, string | null>;
  setRefreshToken: Action<AuthModel, string | null>;
  setCurrentUser: Action<AuthModel, CurrentUserModel | ProxyObjectType>;
  updateAuthToken: Thunk<AuthModel, any>;
  updateRefreshToken: Thunk<AuthModel, any>;
  setPublicKey: Thunk<any>;
  clearPublicKey: Thunk<any>;
  clearAuthToken: Thunk<AuthModel>;
  clearRefreshToken: Thunk<AuthModel>;
  fetchAuthToken: Thunk<AuthModel, any>;
  initRefreshToken: Thunk<AuthModel, boolean>;
  permissions: any;
  setPermissions: Action<AuthModel, any>;
  updatePermissions: Thunk<AuthModel, any>;
  refreshIntervalId: NodeJS.Timeout | null;
  setRefreshIntervalId: Action<AuthModel, NodeJS.Timeout | null>;
}

const AuthStore: AuthModel = {
  permissions: {},
  currentUser: emptyProxyObject,
  authToken: cookie.get(AUTH_TOKEN),
  refreshToken: cookie.get(REFRESH_TOKEN),
  refreshIntervalId: null,

  isLoggedIn: computed((state: any) => state.currentUser !== emptyProxyObject),
  isAdmin: computed((state: any) => state.currentUser.kind === UK.ADMIN),

  setAuthToken: action((state: any, authToken) => {
    state.authToken = authToken;
  }),

  setRefreshToken: action((state: any, refreshToken) => {
    state.refreshToken = refreshToken;
  }),

  setPermissions: action((state: any, permissions) => {
    state.permissions = permissions;
  }),

  setCurrentUser: action((state: any, currentUser) => {
    state.currentUser = currentUser;
  }),

  setRefreshIntervalId: action((state: any, intervalId) => {
    state.refreshIntervalId = intervalId;
  }),

  updatePermissions: thunk((actions, permissions) => {
    let permissionsObj: any = {};
    permissions &&
      permissions
        ?.split(" ")
        ?.map((permission: string) => (permissionsObj[permission] = true));
    actions.setPermissions(permissionsObj);
  }),

  updateAuthToken: thunk((actions, authToken) => {
    // maxAge must be relative seconds from now, not an absolute epoch timestamp.
    const expiryMs = authToken?.expiry instanceof Date
      ? authToken.expiry.getTime()
      : Number(authToken?.expiry) || 0;
    const maxAge = Math.max(Math.floor((expiryMs - Date.now()) / 1000), 0) || undefined;

    getEnv() !== ENV_MAPPER.local
      ? cookie.set(AUTH_TOKEN, authToken.self, {
          domain: cookieDomain(getEnv()),
          path: "/",
          sameSite: "strict",
          secure: true,
          ...(maxAge ? { maxAge } : {}),
        })
      : cookie.set(AUTH_TOKEN, authToken.self, {
          path: "/",
          sameSite: "strict",
        });
    actions.setAuthToken(authToken.self);
  }),

  updateRefreshToken: thunk((actions, refreshToken) => {
    const expiryMs = refreshToken?.expiry instanceof Date
      ? refreshToken.expiry.getTime()
      : Number(refreshToken?.expiry) || 0;
    const maxAge = Math.max(Math.floor((expiryMs - Date.now()) / 1000), 0) || undefined;

    getEnv() !== ENV_MAPPER.local
      ? cookie.set(REFRESH_TOKEN, refreshToken.self, {
          domain: cookieDomain(getEnv()),
          path: "/",
          sameSite: "strict",
          secure: true,
          ...(maxAge ? { maxAge } : {}),
        })
      : cookie.set(REFRESH_TOKEN, refreshToken.self, {
          path: "/",
          sameSite: "strict",
        });
    actions.setRefreshToken(refreshToken.self);
  }),

  setPublicKey: thunk((actions, publicKey) => {
    getEnv() !== ENV_MAPPER.local
      ? cookie.set("publicKey", publicKey, {
          domain: cookieDomain(getEnv()),
          path: "/",
        })
      : cookie.set("publicKey", publicKey, { path: "/" });
  }),

  clearPublicKey: thunk(() => {
    getEnv() !== ENV_MAPPER.local
      ? cookie.remove("publicKey", {
          domain: cookieDomain(getEnv()),
          path: "/",
        })
      : cookie.remove("publicKey", { path: "/" });
  }),

  clearAuthToken: thunk((actions, payload, { getState }) => {
    // Clear any existing refresh interval
    const state = getState();
    if (state.refreshIntervalId) {
      clearInterval(state.refreshIntervalId);
      actions.setRefreshIntervalId(null);
    }

    window.localStorage.clear();
    getEnv() !== ENV_MAPPER.local
      ? cookie.remove(AUTH_TOKEN, {
          domain: cookieDomain(getEnv()),
          path: "/",
        })
      : cookie.remove(AUTH_TOKEN, { path: "/" });
    actions.setAuthToken(null);
    actions.clearPublicKey();
    actions.setCurrentUser(emptyProxyObject);
    window.sessionStorage.clear();
  }),

  clearRefreshToken: thunk((actions, payload, { getState }) => {
    // Clear any existing refresh interval
    const state = getState();
    if (state.refreshIntervalId) {
      clearInterval(state.refreshIntervalId);
      actions.setRefreshIntervalId(null);
    }

    window.localStorage.clear();
    getEnv() !== ENV_MAPPER.local
      ? cookie.remove(REFRESH_TOKEN, {
          domain: cookieDomain(getEnv()),
          path: "/",
        })
      : cookie.remove(REFRESH_TOKEN, { path: "/" });
    actions.setRefreshToken(null);
    actions.setCurrentUser(emptyProxyObject);
  }),

  fetchAuthToken: thunk(
    (actions, { mutation, input, onSuccess, onFailure }) => {
      mutate({
        mutation,
        input: input,
        onSuccess: async function (data: any) {
          let token = {
            self: "",
            expiry: new Date(),
          };
          let refreshToken = {
            self: "",
            expiry: new Date(),
          };
          let unixTime = Number(moment.unix(data.login.refreshExpiresIn || 0));
          let date = new Date(unixTime);
          if (data.login) {
            token.self = data.login.token;
            token.expiry = date;
            refreshToken.self = data.login.refreshToken;
            refreshToken.expiry = date;
          } else if (data.socialAuth) {
            token = data.socialAuth.token;
          }
          await actions.updateAuthToken(token);
          await actions.updateRefreshToken(refreshToken);
          onSuccess(data);
        },
        onFailure: function (messages: []) {
          console.log("Failed in fetching tokens");
          actions.clearAuthToken();
          actions.clearRefreshToken();
          onFailure(messages);
        },
      });
    },
  ),

  initRefreshToken: thunk((actions, initialFetch = true, { getState }) => {
    const state = getState();
    if (state.refreshIntervalId) {
      console.log("🔄 Clearing existing refresh interval before creating new one");
      clearInterval(state.refreshIntervalId);
      actions.setRefreshIntervalId(null);
    }

    const getExpiryDate = (exp: number) => new Date((exp || 0) * 1000);
  
    // First refresh on load
    if (initialFetch && cookie.get(REFRESH_TOKEN)) {
      mutate({
        mutation,
        input: { refreshToken: cookie.get(REFRESH_TOKEN) },
        onSuccess: function (data: any) {
          const exp = data.refreshToken.payload.exp;
          const expiryDate = getExpiryDate(exp);
          actions.updateAuthToken({
            self: data.refreshToken.token,
            expiry: expiryDate,
          });
  
          actions.updateRefreshToken({
            self: data.refreshToken.refreshToken,
            expiry:  new Date(Date.now() + 24 * 60 * 60 * 1000),
          });
        },
        onFailure: function (messages: any) {
          console.log("failed in fetching tokens in calling refresh token");
          // Don't immediately clear tokens on initial refresh failure
          // This could be due to timing issues or temporary server problems
          // Only clear if this is not the initial fetch (i.e., it's a scheduled refresh)
          if (!initialFetch) {
            actions.clearAuthToken();
            actions.clearRefreshToken();
          } else {
            console.log("Initial refresh token failure - not clearing tokens, will retry on next interval");
          }
        },
      });
    }

    // Set up the interval and store its ID
    const intervalId = setInterval(() => {
      if (cookie.get(REFRESH_TOKEN)) {
        mutate({
          mutation,
          input: { refreshToken: cookie.get(REFRESH_TOKEN) },
          onSuccess: function (data: any) {
            const exp = data.refreshToken.payload.exp;
            const expiryDate = getExpiryDate(exp);
            actions.updateAuthToken({
              self: data.refreshToken.token,
              expiry: expiryDate,
            });
            actions.updateRefreshToken({
              self: data.refreshToken.refreshToken,
              expiry:  new Date(Date.now() + 24 * 60 * 60 * 1000),
            });
          },
          onFailure: function (messages: any) {
            console.log(
              "failed in fetching tokens in after expiry of authToken",
            );
             if (!navigator.onLine) {
              console.warn("[Auth] Periodic refresh skipped — device is offline, will retry on next interval");
              return;
            }
            // Only clear tokens if this is a scheduled refresh failure
            // This indicates the refresh token is actually invalid/expired
            actions.clearAuthToken();
            actions.clearRefreshToken();
          },
        });
    }
    }, TOKEN_TTL);

    actions.setRefreshIntervalId(intervalId);
  }),
};

export default AuthStore;
